/**
 * VectorStatus — Vector index statistics.
 */

import { useState, useEffect } from 'react';
import { MetricCard } from '../../../components/MetricCard';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import apiClient from '../../../api/client';
import type { VectorStatusResponse } from '../../../api/types';

export function VectorStatus() {
  const { user } = useAuth();
  const [vectorStatus, setVectorStatus] = useState<VectorStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/cti/vectors/status', {
        params: { tenant_id: user.tenantId },
      });
      setVectorStatus(response.data);
    } catch {
      setError('Failed to load vector index status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <LoadingState message="Loading vector status..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  if (!vectorStatus) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-heading-lg font-bold text-slate-text">Vector Index Status</h1>
          <p className="text-body text-slate-muted mt-1">
            Knowledge base embedding statistics
          </p>
        </div>
        <div className="glass-card text-center py-12">
          <p className="text-body text-slate-muted">
            No vector data available. Process documents to populate the vector index.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Vector Index Status</h1>
        <p className="text-body text-slate-muted mt-1">
          Knowledge base embedding statistics
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Vectors"
          value={vectorStatus.total_vectors.toLocaleString()}
          subtitle="Embedded chunks"
        />
        <MetricCard
          title="Documents Indexed"
          value={vectorStatus.total_documents_indexed}
          subtitle="Source documents"
        />
        <MetricCard
          title="Dimensions"
          value={vectorStatus.dimensions}
          subtitle="Embedding size"
        />
        <MetricCard
          title="Last Updated"
          value={new Date(vectorStatus.last_updated).toLocaleDateString()}
          subtitle={new Date(vectorStatus.last_updated).toLocaleTimeString()}
        />
      </div>

      {/* Index details */}
      <div className="glass-card">
        <h3 className="text-section-sm font-bold text-slate-text mb-4">Index Configuration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[16px] font-semibold text-slate-muted">Index Name</p>
            <p className="text-body font-medium text-slate-text font-mono">{vectorStatus.index_name}</p>
          </div>
          <div>
            <p className="text-[16px] font-semibold text-slate-muted">Embedding Model</p>
            <p className="text-body font-medium text-slate-text">amazon.titan-embed-text-v2:0</p>
          </div>
          <div>
            <p className="text-[16px] font-semibold text-slate-muted">Distance Metric</p>
            <p className="text-body font-medium text-slate-text">Cosine Similarity</p>
          </div>
          <div>
            <p className="text-[16px] font-semibold text-slate-muted">Storage Backend</p>
            <p className="text-body font-medium text-slate-text">Amazon S3 Vectors</p>
          </div>
        </div>
      </div>
    </div>
  );
}
